import React, { useEffect, useState } from "react";
import styles from "./errors-page.module.css";
import { Table, TableColumn, TableState } from "@/components/Table/Table";
import Skeleton from "@/components/Skeleton/Skeleton";
import { getInventoryErrors } from "@/api/inventory";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { InventoryError, ProductCondition } from "@boxem/core-api-schema";
import Button from "@/components/forms/Button/Button";
import { Modal, ModalBody, ModalHeader } from "@/components/Modal/Modal";
import IconError from "@/components/icons/IconError";
import Select from "@/components/forms/Select/Select";
import { LocalStorageKeys, getLocalStorageItem } from "@/utils/localStorageUtils";
import { formatValue } from "@/utils/formatUtils";
import Pagination from "@/components/Pagination/Pagination";
import { productConditionToString } from "@/app/fba/utils";
import StatusBadge from "@/components/StatusBadge/StatusBadge";

interface ErrorsTableState {
  sortedColumnId: string | undefined;
  sortDirection: 'asc' | 'desc' | undefined;
  page: number;
  limit: number;
}

type ErrorsPageProps = {};

const ErrorsPage = ({}: ErrorsPageProps) => {
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const [loadingListings, setLoadingListings] = useState<boolean>(false);
  const [errors, setErrors] = useState<InventoryError[]>([]);
  const [status, setStatus] = useState<string>("LISTED");
  const [listingsModalOpen, setListingsModalOpen] = useState<{ open: boolean, index: string }>({ open: false, index: "0" });
  const tableName = "listingErrorsTable";
  const [tableState, setTableState] = useState<ErrorsTableState>(() => {
    const tableProfile: TableState = getLocalStorageItem(`${LocalStorageKeys.tableStatePrefix}${tableName}`);

    return {
      sortedColumnId: tableProfile?.sortedColumnId ?? undefined,
      sortDirection: tableProfile?.sortDirection ?? undefined,
      page: 1,
      limit: 10,
    };
  });

  const columns: TableColumn<InventoryError>[] = [
    {
      title: 'Instances',
      dataKey: 'instances',
      id: 'instances',
      sortable: true,
      width: '3rem',
      alignment: 'left',
      renderRowCell: (row) => formatValue(row.getCellData('instances'), 'number'),
      renderSkeleton: () => (
        <div className={styles.listingItem}>
          <Skeleton type='line-short' />
        </div>
      )
    },
    {
      title: 'Fba',
      dataKey: 'fba',
      id: 'fba',
      sortable: false,
      width: '2rem',
      alignment: 'left',
      renderRowCell: (row) => formatValue(row.getCellData('fbaInstances'), 'number'),
      renderSkeleton: () => (
        <div className={styles.listingItem}>
          <Skeleton type='line-short' />
        </div>
      )
    },
    {
      title: 'Fbm',
      dataKey: 'fbm',
      id: 'fbm',
      sortable: false,
      width: '2rem',
      alignment: 'left',
      renderRowCell: (row) => formatValue(row.getCellData('fbmInstances'), 'number'),
      renderSkeleton: () => (
        <div className={styles.listingItem}>
          <Skeleton type='line-short' />
        </div>
      )
    },
    {
      title: 'Code',
      dataKey: 'code',
      id: 'code',
      sortable: false,
      width: '2rem',
      alignment: 'left',
      renderRowCell: (row) => row.getCellData('error').code,
      renderSkeleton: () => (
        <div className={styles.listingItem}>
          <Skeleton type='line-short' />
        </div>
      ),
    },
    {
      title: 'Severity',
      dataKey: 'severity',
      id: 'severity',
      sortable: false,
      width: '2rem',
      alignment: 'left',
      renderRowCell: (row) => row.getCellData('error').severity,
      renderSkeleton: () => (
        <div className={styles.listingItem}>
          <Skeleton type='line-short' />
        </div>
      ),
    },
    {
      title: 'Categories',
      dataKey: 'categories',
      id: 'categories',
      sortable: false,
      width: '4.5rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.listingItem}>
          {row.getCellData('error').categories.join(",") || "--"}
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.listingItem}>
          <Skeleton type='line-medium' />
        </div>
      ),
    },
    {
      title: 'Message',
      dataKey: 'message',
      id: 'message',
      sortable: false,
      width: '15rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.listingItem}>
          {row.getCellData('error').message}
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.listingItem}>
          <Skeleton type='line-long' />
        </div>
      ),
    },
    {
      title: 'Listings',
      dataKey: 'listings',
      id: 'listings',
      sortable: false,
      width: '2rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.listingItem}>
          <Button
            type="secondary"
            onClick={() => setListingsModalOpen({ open: true, index: row.getCellData('index').toString() })}
          >
            View
          </Button>
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.listingItem}>
          <Skeleton type='line-long' />
        </div>
      ),
    },
  ];

  const getFilteredInventoryErrors = (inventoryErrors: InventoryError[]) => {
    const columnDataKey = columns?.find((col) => col.id === tableState.sortedColumnId)?.dataKey as string;

    return [...inventoryErrors].sort((a, b) => {
      if (tableState.sortedColumnId) {
        let aValue = 0;
        let bValue = 0;

        aValue = a[columnDataKey];
        bValue = b[columnDataKey];

        if (tableState.sortDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      }

      return 0;
    });
  };

  const handleSortChange = async (columnId: string, direction: 'asc' | 'desc') => {
    setTableState({
      ...tableState,
      sortedColumnId: columnId,
      sortDirection: direction,
      page: 1,
    });
  };

  const handlePageSizeChange = async (newPageSize: number) => {
    setTableState({
      ...tableState,
      page: 1,
      limit: newPageSize,
    });
  };

  const handlePageChange = async (newPage: number) => {
    setTableState({
      ...tableState,
      page: newPage,
    });
  };

  useEffect(() => {
    setErrors(getFilteredInventoryErrors(errors))
  }, [
    tableState.page,
    tableState.limit,
    tableState.sortDirection,
    tableState.sortedColumnId,
  ]);

  useEffect(() => {
    setLoadingListings(true);

    getInventoryErrors(status)
      .then(data => setErrors(getFilteredInventoryErrors(data.data)))
      .catch(err => handleErrorResponseGenerically(err))
      .finally(() => setLoadingListings(false));
  }, [status]);

  const filteredInventoryErrors = errors.slice((tableState.page - 1) * tableState.limit, tableState.page * tableState.limit);
  const error = errors.find(err => err.error.code === listingsModalOpen.index);

  return (
    <div className={styles.errorsContainer}>
      <div className={styles.errorsTableContainer}>
        <Table
          tableName={tableName}
          className={styles.table}
          data={filteredInventoryErrors}
          columns={columns}
          isLoading={loadingListings}
          skeletonRows={tableState.limit}
          sortDirection={tableState.sortDirection}
          sortedColumnId={tableState.sortedColumnId}
          onSort={handleSortChange}
          showSort
          header={
            <h1>Listing Errors</h1>
          }
          headerControls={
            <>
              <Select
                value={status}
                options={["LISTED", "ERROR", "INACTIVE", "INCOMPLETE"]}
                onChange={v => setStatus(v)}
              />
            </>
          }
          footer={
            <Pagination
              total={errors.length || 0}
              pageSize={tableState.limit}
              onChangePageSize={handlePageSizeChange}
              page={tableState.page}
              onChangePage={handlePageChange}
              pageSizeOptions={[10, 20, 50, 100]}
            />
          }
        />
      </div>

      <Modal
        open={listingsModalOpen.open}
        onOpenChange={() => {}}
        onClose={() => setListingsModalOpen({ open: false, index: "0" })}
        className={styles.modal}
      >
        <ModalHeader>
          <h1>({error?.instances}) Listings with error:</h1>
          <div className={styles.errorContainer}>
            <IconError size={22} className={styles.errorIcon} currentColor />
            <h5 className={styles.errorCodeText}>{error?.error?.severity}: [{error?.error?.code}]</h5>
            <h6 className={styles.errorText}>{error?.error?.message}</h6>
          </div>
          <div className={styles.errorContainer}>
            {JSON.stringify(error?.error)}
          </div>
        </ModalHeader>
        <ModalBody className={styles.modelBody}>
          {error?.listings.map((listing, index) => {
            return (
              <div key={index} className={styles.header}>
                <div className={styles.metaDataContainer}>
                  <span className={styles.metaDataTitle}>
                    {listing.name}
                  </span>
                  <div className={styles.metaDataSubContainer}>
                    <div className={styles.skuAndCondition}>
                      <span className={styles.subTitleContainer}>
                        <span className={styles.subMetaTitle}>
                          ASIN:
                        </span>
                        <span className={styles.subMetaValue}>
                          {listing.asin}
                        </span>
                      </span>
                      <span className={styles.dot}>
                        •
                      </span>
                      <span className={styles.subTitleContainer}>
                        <span className={styles.subMetaTitle}>
                          SKU:
                        </span>
                        <span className={styles.subMetaValue}>
                          {listing.sku}
                        </span>
                      </span>
                      <span className={styles.dot}>
                        •
                      </span>
                      <span className={styles.subTitleContainer}>
                        <span className={styles.subMetaTitle}>
                          Condition:
                        </span>
                        <span className={styles.subMetaValue}>
                          {productConditionToString(listing.condition as ProductCondition)}
                        </span>
                      </span>
                      <span className={styles.dot}>
                        •
                      </span>
                      <span className={styles.subTitleContainer}>
                        <StatusBadge
                          status={listing.fulfillmentType === "MFN" || listing.fulfillmentType === "FBM" ? "danger" : "warning"}
                          label={listing.fulfillmentType === "MFN" || listing.fulfillmentType === "FBM" ? "FBM" : "FBA"}
                          className={styles.statusBadge}
                        />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ErrorsPage;
